import type { ReactNode } from "react";
import { Columns2Icon, Rows2Icon } from "lucide-react";
import type { FindingPdfEdit } from "@repo/shared/findingChanges";
import { Checkbox } from "../../../ui/checkbox";
import { ToggleGroup, Toggle } from "../../../ui/toggle-group";

export function FindingChangesToolbar({
  edits,
  navigation,
  highlight,
  onHighlightChange,
  stacked,
  onStackedChange,
}: {
  edits: ReadonlyArray<FindingPdfEdit>;
  navigation?: ReactNode;
  highlight: boolean;
  onHighlightChange: (checked: boolean) => void;
  stacked: boolean;
  onStackedChange: (stacked: boolean) => void;
}) {
  const pageCount = new Set(edits.flatMap((edit) => [edit.beforePage, edit.afterPage])).size;
  return (
    <div
      className="flex min-w-0 items-center gap-3 py-1"
      role="region"
      aria-label="Change review controls"
    >
      {navigation ?? (
        <span className="whitespace-nowrap text-xs font-medium tabular-nums">
          {edits.length} {edits.length === 1 ? "change" : "changes"}
          <span className="mx-2 text-muted-foreground/50">·</span>
          <span className="font-normal text-muted-foreground">
            {pageCount} {pageCount === 1 ? "page" : "pages"}
          </span>
        </span>
      )}
      <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-xs">
        <Checkbox checked={highlight} onCheckedChange={onHighlightChange} />
        <span>Highlight<span className="sr-only sm:not-sr-only"> passages</span></span>
      </label>
      <ToggleGroup
        value={[stacked ? "stacked" : "split"]}
        onValueChange={(value) => {
          if (value.length) onStackedChange(value[0] === "stacked");
        }}
        variant="outline"
        size="sm"
        aria-label="PDF comparison layout"
        className="hidden md:flex"
      >
        <Toggle value="split" aria-label="Split" title="Side by side">
          <Columns2Icon className="size-3.5" />
          <span className="sr-only">Split</span>
        </Toggle>
        <Toggle value="stacked" aria-label="Stacked" title="Stacked">
          <Rows2Icon className="size-3.5" />
          <span className="sr-only">Stacked</span>
        </Toggle>
      </ToggleGroup>
    </div>
  );
}
